import { useState, useRef, useEffect, useCallback, ReactNode } from "react";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { Box, Tooltip } from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import Circle from "@mui/icons-material/Circle";

import { calculateCallStatus } from "view/calls/utils/CallUtils";

import { CallSideviewerCallFragment } from "__generated__/view/calls/graphql/call.fragments.generated";

const CallSideviewerTabs = ({
	calls,
	nextCallId,
	selectedCallId,
	onChange,
}: {
	calls: CallSideviewerCallFragment[];
	nextCallId: number | null;
	selectedCallId: number | null;
	onChange: (callId: number) => void;
}) => {

	const session = useStoreState(s => s.session);
	const containerRef = useRef<HTMLDivElement>(null);
	const [ visibleCount, setVisibleCount ] = useState(3);

	const calculateVisibleCount = useCallback(() => {
		if (!containerRef.current) {
			return;
		}
		const available = containerRef.current.offsetWidth - 80;
		setVisibleCount(Math.max(1, Math.floor(available / 120)));
	}, []);

	useEffect(() => {
		calculateVisibleCount();
		const observer = new ResizeObserver(calculateVisibleCount);
		if (containerRef.current) {
			observer.observe(containerRef.current);
		}
		return () => observer.disconnect();
	}, [ calculateVisibleCount ]);

	useEffect(() => {
		const index = calls.findIndex(c => c.id === selectedCallId);
		if (index !== -1) {
			setStartIndex(Math.floor(index / visibleCount) * visibleCount);
		}
	}, [ visibleCount, calls ]);

	const [ startIndex, setStartIndex ] = useState(() => {
		const index = calls.findIndex(c => c.id === selectedCallId);
		return index !== -1 ? Math.floor(index / visibleCount) * visibleCount : 0;
	});

	const visibleItems = calls.slice(startIndex, startIndex + visibleCount);
	const skeletonsNeeded = visibleCount - visibleItems.length;

	const nextTabs = () => {
		if (startIndex + visibleCount < calls.length) {
			setStartIndex(startIndex + visibleCount);
		}
	};

	const prevTabs = () => {
		if (startIndex - visibleCount >= 0) {
			setStartIndex(startIndex - visibleCount);
		}
	};

	return (
		<Box ref={containerRef} sx={{ alignItems: "center", display: "flex", gap: 1 }}>
			<StyledButton onClick={prevTabs} disabled={startIndex === 0}>
				<KeyboardArrowLeft />
			</StyledButton>

			<StyledTabs>
				{visibleItems.map(call => {
					const isActive = selectedCallId === call.id;
					const status = calculateCallStatus(call, session);
					const locationText = call.location?.displayCode || call.location?.code || call.location?.description;

					return (
						<StyledTab
							isActive={isActive}
							onClick={() => {
								if (call.id !== selectedCallId) {
									onChange(call.id);
								}
							}}
							key={call.id}>
							<StyledDescription>
								{locationText}
							</StyledDescription>

							<StyledStatus color={status.color}>
								<Tooltip title={status.status}>
									<Box
										component="span"
										sx={{
											alignItems: "center",
											cursor: "pointer",
											display: "flex",
											height: "100%",
											justifyContent: "center",
											width: "100%",
										}}>
										{status.symbol}
									</Box>
								</Tooltip>
							</StyledStatus>

							{nextCallId === call.id && (
								<StyledNextCallIndicator>
									<Tooltip title={intl.formatMessage({ id: "STATUSRULE_CALL_NEXT_CALL" })}>
										<Circle sx={{ fontSize: 11 }} />
									</Tooltip>
								</StyledNextCallIndicator>
							)}
						</StyledTab>
					);
				})}

				{Array.from({ length: skeletonsNeeded }).map((_, i) => <StyledFiller key={`filler-${i}`} />)}
			</StyledTabs>

			<StyledButton onClick={nextTabs} disabled={startIndex + visibleCount >= calls.length}>
				<KeyboardArrowRight />
			</StyledButton>
		</Box>
	);
};

const StyledButton = ({
	onClick,
	disabled,
	children,
}: {
	onClick: () => void;
	disabled: boolean;
	children: ReactNode;
}) => (
	<Box
		component="button"
		onClick={onClick}
		disabled={disabled}
		sx={{
			background: "none",
			border: "none",
			borderRadius: "50%",
			cursor: "pointer",
			display: "flex",
			padding: 0.5,
			transition: "background 0.2s",

			"&:hover:not(:disabled)": {
				backgroundColor: "#f3f4f6",
			},

			"&:disabled": {
				cursor: "not-allowed",
				opacity: 0.3,
			},
		}}>
		{children}
	</Box>
);

const StyledTabs = ({ children }: { children: ReactNode }) => (
	<Box sx={{ display: "flex", flex: 1, height: 50, gap: 1 }}>
		{children}
	</Box>
);

const StyledTab = ({
	isActive,
	onClick,
	children,
}: {
	isActive: boolean;
	onClick: () => void;
	children: ReactNode
}) => (
	<Box
		component="button"
		onClick={onClick}
		sx={{
			backgroundColor: "background.paper",
			borderWidth: 1,
			borderStyle: "solid",
			borderColor: isActive ? "#1890ff" : "#d9d9d9",
			borderRadius: 0.5,
			cursor: "pointer",
			flex: 1,
			opacity: isActive ? 1 : 0.5,
			overflow: "hidden",
			position: "relative",
			transition: "all 0.2s",

			"&:hover": {
				borderColor: isActive ? "#1890ff" : "#9c9c9c",
			},
		}}>
		{children}
	</Box>
);

const StyledDescription = ({ children }: { children: ReactNode }) => (
	<Box
		component="div"
		sx={{
			display: "-webkit-box",
			fontSize: 13,
			fontWeight: 500,
			lineHeight: 1.2,
			paddingRight: 2,
			paddingLeft: 1,
			WebkitBoxOrient: "vertical",
			WebkitLineClamp: 2,
			overflow: "hidden",
			wordBreak: "break-word",
			height: "auto",
			maxHeight: "calc(2 * 1.2em)",
		}}>
		{children}
	</Box>
);

const StyledStatus = ({ color, children }: { color: string; children: ReactNode }) => (
	<Box
		sx={{
			alignItems: "center",
			backgroundColor: color,
			cursor: "pointer",
			color: "#FFF",
			display: "flex",
			fontSize: 13,
			fontWeight: 600,
			justifyContent: "center",
			height: "100%",
			position: "absolute",
			right: 0,
			top: 0,
			width: 16,
		}}>
		{children}
	</Box>
);

const StyledNextCallIndicator = ({ children }: { children: ReactNode }) => (
	<Box
		sx={{
			alignItems: "center",
			cursor: "pointer",
			display: "flex",
			height: 16,
			justifyContent: "center",
			left: 0,
			position: "absolute",
			top: 0,
			width: 16,
		}}>
		{children}
	</Box>
);

const StyledFiller = () => (
	<Box
		sx={{
			backgroundImage: "repeating-linear-gradient(45deg, #e8e8e8, #e8e8e8 4px, #ababab 4px, #ababab 8px);",
			opacity: 0.3,
			flex: 1,
		}} />
);

export default CallSideviewerTabs;
